import { PRICE_OPTION_NAMES } from '@/lib/constants';
import { type Page, PageStatus } from '@/types/store';
import { FUNNEL_APPLICABLE_PRODUCT_TYPES } from '../constants/funnels.constants';

type LooseRecord = Record<string, unknown>;

export interface FunnelProductEligibility {
    eligible: boolean;
    reasons: string[];
}

function asRecord(value: unknown): LooseRecord {
    return value && typeof value === 'object' ? (value as LooseRecord) : {};
}

function hasActiveAffiliateShares(page: Page): boolean {
    const shares = page.data?.affiliate_shares ?? [];

    return shares.some(share => {
        const record = share as typeof share & { percent?: number; status?: string };
        const percent = record.percent ?? record.percentage ?? 0;
        return percent > 0 && record.status !== 'deleted';
    });
}

function hasResellRightsEnabled(page: Page): boolean {
    const resellRights = page.data?.resell_rights;
    return !!resellRights?.enabled && (resellRights.percentage ?? 0) > 0;
}

function hasEnabledAnnualPriceOption(page: Page): boolean {
    const priceOptions = asRecord(page.data?.product?.price?.price_options);
    const annualOption = asRecord(priceOptions[PRICE_OPTION_NAMES.ANNUAL_SUBSCRIPTION]);
    return annualOption.enabled === true;
}

function hasEnabledCountdownTimer(page: Page): boolean {
    const countdownTimer = asRecord(page.data?.countdown_timer);
    return countdownTimer.show_timer === true || typeof countdownTimer.end_date === 'string';
}

function hasCurrencyOverride(page: Page): boolean {
    const currencyOverride = asRecord(asRecord(page.data).currency_override);
    return typeof currencyOverride.currency === 'string' && currencyOverride.currency.length > 0;
}

function hasCommunityIntegration(page: Page): boolean {
    return !!page.data?.community_integration?.enabled;
}

export function getFunnelProductEligibility(page: Page): FunnelProductEligibility {
    const reasons: string[] = [];

    if (page.status !== PageStatus.PUBLISHED) {
        reasons.push('Product must be published.');
    }

    if (!FUNNEL_APPLICABLE_PRODUCT_TYPES.has(page.type)) {
        reasons.push('Product type is not supported in funnels.');
    }

    if (hasResellRightsEnabled(page) || hasActiveAffiliateShares(page)) {
        reasons.push('Affiliate products are not supported in funnels.');
    }

    const price = asRecord(page.data?.product?.price);
    const stanFlexiblePayment = asRecord(price.stan_flexible_payment);
    if (stanFlexiblePayment.enabled === true) {
        reasons.push('Payment plans are not supported in funnels.');
    }

    if (hasEnabledAnnualPriceOption(page)) {
        reasons.push('Annual subscription discounts are not supported in funnels.');
    }

    if (hasCurrencyOverride(page)) {
        reasons.push('Currency overrides are not supported in funnels.');
    }

    if (hasCommunityIntegration(page)) {
        reasons.push('Community integrations are not supported in funnels.');
    }

    if (hasEnabledCountdownTimer(page)) {
        reasons.push('Countdown timers are not supported in funnels.');
    }

    return {
        eligible: reasons.length === 0,
        reasons,
    };
}
